import { useDictation } from '@/hooks/useDictation';
import { useAssistantStatus } from '@/hooks/assistant';
import { Spinner } from '@/components/ui';
import { CloseIcon, MicIcon } from '@/components/ui/icons';

/**
 * Speak instead of typing, for the composers that talk to a model.
 *
 * Tap to start, tap to stop; the words land in the draft rather than being
 * sent, so nothing reaches a model that the speaker has not read. Holding the
 * recording state in `useDictation` is what lets this be dropped beside any
 * composer without each one relearning the microphone teardown rules.
 *
 * Renders nothing at all when the deployment cannot transcribe. That is the
 * same bargain the rest of the assistant makes — see `AssistantStatus.stt` —
 * and it is why this checks rather than the callers: a microphone that records
 * and then fails is worse than no microphone.
 */
export function DictationButton({
  onTranscript,
  disabled,
  label = 'Dictate',
}: {
  /** Called with the words. Append to the draft; do not send. */
  onTranscript: (text: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const status = useAssistantStatus().data;
  const dictation = useDictation(onTranscript);

  if (!status?.enabled || !status.stt) return null;

  const recording = dictation.phase === 'recording';
  const busy = dictation.phase === 'transcribing';

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => (recording ? dictation.stop(true) : void dictation.start())}
        aria-label={recording ? 'Stop dictating and insert the words' : label}
        title={recording ? 'Stop and transcribe' : label}
        className={`btn btn-sm ${recording ? 'btn-danger' : 'btn-ghost'}`}
      >
        {busy ? <Spinner /> : <MicIcon size="sm" />}
        {recording && <span className="tabular-nums">{dictation.seconds}s</span>}
        {busy && <span>Transcribing…</span>}
      </button>

      {/* Only while recording: a clip you have decided against should be
          droppable without paying to have it transcribed first. */}
      {recording && (
        <button
          type="button"
          onClick={() => dictation.stop(false)}
          aria-label="Discard this recording"
          title="Discard"
          className="btn btn-ghost btn-sm"
        >
          <CloseIcon size="sm" />
        </button>
      )}

      {dictation.error && (
        <span className="text-[11px] text-red-300">{dictation.error}</span>
      )}
    </div>
  );
}
